import { useState, useEffect } from "react";

import toast from "react-hot-toast";
import styled from "styled-components";

import { trackAction } from "~/analytics";
import { ButtonLink } from "~/components/Button";
import { Separator } from "~/components/Elements";
import * as Form from "~/components/Form";
import Loading from "~/components/Loading";
import Modal from "~/components/Modal";
import Spacer, { Axis } from "~/components/Spacer";
import { Title } from "~/components/Text";
import { postTxAccept, postTxBulk } from "~/endpoints/transactions";
import { Transaction, TransactionStatus, TransactionType } from "~/endpoints/transactions/types";
import { useTfSimpleVaspInfo } from "~/endpoints/trustFramework";
import { TransactionWrap } from "~/hooks/use-transaction";
import ConfirmIncomingTxModal from "~/templates/transactions/components/ConfirmIncomingTxModal";
import IncomingTxDisplay from "~/templates/transactions/components/IncomingTxDisplay";

const FlexDiv = styled.div`
  display: flex;
  justify-content: center;
`;
const CenteredText = styled.div`
  text-align: center;
`;
const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;

type AcceptIncomingTxModalProps = {
  transaction: TransactionWrap; //lluis maybe transaction.id is enough?
  onClose: () => void;
};

export default function AcceptIncomingTxModal({ transaction, onClose }: AcceptIncomingTxModalProps) {
  const [isBulk, setIsBulk] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBlockchainAddressConfirmationNeeded, setIsBlockchainAddressConfirmationNeeded] = useState<
    boolean | undefined
  >(undefined);
  const trackProps = transaction?.beneficiaryVASPdid ? { group_vasp: transaction.beneficiaryVASPdid } : undefined;

  const { data: externalVASP, isLoading: loadingVaspInfo } = useTfSimpleVaspInfo(transaction.originatorVASPdid);

  useEffect(() => {
    if (transaction) {
      setIsBlockchainAddressConfirmationNeeded(transaction.status === TransactionStatus.SENT);
    }
  }, [transaction]);

  const handleOnAddressConfirmed = (updatedTransaction: Transaction) => {
    setIsBlockchainAddressConfirmationNeeded(false);
    const wasTransactionAutoAccepted = updatedTransaction.status === TransactionStatus.ACCEPTED;
    const wasTransactionAutoRejected = updatedTransaction.status === TransactionStatus.REJECTED;
    const wasTransactionAutoDeclined = updatedTransaction.status === TransactionStatus.DECLINED;
    if (wasTransactionAutoAccepted) {
      // we don't need to show the Accept Modal in this case
      toast.success("Transfer was auto-accepted and moved to COMPLETED");
      trackAction("transaction-was-auto-accepted", trackProps);
      onClose();
    }
    if (wasTransactionAutoRejected) {
      // we don't need to show the Accept Modal in this case
      toast.success("Transfer was auto-rejected and moved to COMPLETED");
      trackAction("transaction-was-auto-rejected", trackProps);
      onClose();
    }
    if (wasTransactionAutoDeclined) {
      // we don't need to show the Accept Modal in this case
      toast.success("Transfer was auto-declined and moved to COMPLETED");
      trackAction("transaction-was-auto-declined");
      onClose();
    }
  };

  const acceptTransaction = async (transactionId: string) => {
    /*This is the regular flow for our customers */
    if (isBulk) {
      const transactions = await postTxBulk({
        action: "accept",
        originatorVASPdid: transaction.originatorVASPdid,
        beneficiaryVASPdid: transaction.beneficiaryVASPdid,
      });
      toast.success(`${transactions.length} transfers accepted and moved to COMPLETED`);
      transactions.forEach(() => trackAction("user-accepted-incoming-transaction", trackProps));
    } else {
      await postTxAccept(transactionId);
      trackAction("user-accepted-incoming-transaction", trackProps);
      toast.success("Transfer accepted and moved to COMPLETED");
    }
  };

  const handleClickAccept = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      if (transaction) {
        await acceptTransaction(transaction.id);
      }
    } catch (err) {
      toast.error(`Oops an unexpected error occurred: ${err} `);
      trackAction("user-accepted-incoming-transaction-error");
    }
    setIsLoading(false);
    onClose();
  };

  if (!transaction || isBlockchainAddressConfirmationNeeded === undefined || loadingVaspInfo || !externalVASP) {
    return <Loading />;
  }

  if (isBlockchainAddressConfirmationNeeded === true) {
    return (
      <ConfirmIncomingTxModal
        transaction={transaction}
        onAddressConfirmed={(updatedTransaction) => {
          handleOnAddressConfirmed(updatedTransaction);
        }}
        onClose={onClose}
      />
    );
  }

  if (transaction && isBlockchainAddressConfirmationNeeded === false && externalVASP) {
    const originatorVaspName = externalVASP?.name;

    return (
      <Modal active onClose={onClose}>
        <Modal.Header>
          <Title as="h1" align="centered">
            This is a Travel Rule transfer
          </Title>
        </Modal.Header>
        <Modal.Body
          styles={{
            maxWidth: 1000,
          }}
        >
          {isLoading && <Loading />}

          <FlexDiv>
            <IncomingTxDisplay
              customerLogo={transaction.beneficiaryVASP?.logo}
              customerDisplayText={transaction.beneficiaryDisplay}
              counterpartyLogo={transaction.originatorVASP?.logo}
              counterpartyDisplayText={transaction.originatorDisplay}
              counterPartyVASP={externalVASP}
            />
          </FlexDiv>

          <Spacer axis={Axis.VERTICAL} size={40} />
          <CenteredText>
            {`By accepting you agree to receive the transfer from `}
            <span className="has-text-weight-bold">{`${originatorVaspName || externalVASP?.did}`}</span>
          </CenteredText>

          <Separator color="link" weight="light" />
          {transaction.type === TransactionType.TRAVELRULE && (
            <Form.CheckboxLabel>
              <Form.CheckboxInput checked={isBulk} onChange={(e) => setIsBulk(e.target.checked)}></Form.CheckboxInput>
              <Form.CheckboxText>
                Accept all data transfers from <b>{originatorVaspName || externalVASP?.did}</b> remaining in the Inbox
              </Form.CheckboxText>
            </Form.CheckboxLabel>
          )}
        </Modal.Body>

        <Modal.Footer>
          <SpaceBetween>
            <ButtonLink onClick={onClose}>{"Cancel"}</ButtonLink>
            <ButtonLink color="success" onClick={handleClickAccept}>
              Accept and store data
            </ButtonLink>
          </SpaceBetween>
        </Modal.Footer>
      </Modal>
    );
  }

  return <Loading />;
}
